import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfigurations } from '@/hooks/useConfigurations';
import { useStartConversation } from '@/hooks/useMessaging';
import { whatsappTemplatesApi } from '@/lib/api/whatsapp-templates.api';

import type { TemplateComponent } from '@/lib/api/whatsapp-templates.api';

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (conversationId: string) => void;
}

const extractVariables = (text?: string): number => {
  if (!text) return 0;
  const matches = text.match(/\{\{\d+\}\}/g);
  return matches?.length ?? 0;
};

const getTemplateParams = (
  components: unknown,
): Array<{ type: 'HEADER' | 'BODY'; format?: string; count: number }> => {
  if (!Array.isArray(components)) return [];
  const params: Array<{ type: 'HEADER' | 'BODY'; format?: string; count: number }> = [];

  for (const comp of components as TemplateComponent[]) {
    if (comp.type === 'HEADER') {
      if (comp.format === 'IMAGE' || comp.format === 'VIDEO' || comp.format === 'DOCUMENT') {
        params.push({ type: 'HEADER', format: comp.format, count: 1 });
      } else {
        const count = extractVariables(comp.text);
        if (count > 0) params.push({ type: 'HEADER', count });
      }
    }
    if (comp.type === 'BODY') {
      const count = extractVariables(comp.text);
      if (count > 0) params.push({ type: 'BODY', count });
    }
  }
  return params;
};

export const NewConversationDialog = ({
  open,
  onOpenChange,
  onCreated,
}: NewConversationDialogProps) => {
  const [channel, setChannel] = useState<'WHATSAPP' | 'EMAIL'>('WHATSAPP');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [configurationId, setConfigurationId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const { data: configs } = useConfigurations();
  const startConversation = useStartConversation();
  const { data: templates } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => whatsappTemplatesApi.list(),
    enabled: open,
  });

  const approvedTemplates = templates?.filter((t) => t.status === 'APPROVED') ?? [];
  const selectedTemplate = approvedTemplates.find((t) => t.name === templateName);

  const templateParams = selectedTemplate ? getTemplateParams(selectedTemplate.components) : [];

  const buildComponents = () => {
    const components: Array<{
      type: string;
      parameters?: Array<{ type: string; text?: string; image?: { link: string } }>;
    }> = [];

    for (const param of templateParams) {
      if (param.type === 'HEADER' && (param.format === 'IMAGE' || param.format === 'VIDEO')) {
        const url = paramValues[`header_0`] ?? '';
        components.push({
          type: 'header',
          parameters: [
            param.format === 'IMAGE'
              ? { type: 'image', image: { link: url } }
              : { type: 'text', text: url },
          ],
        });
      } else {
        const parameters: Array<{ type: string; text?: string }> = [];
        for (let i = 0; i < param.count; i++) {
          parameters.push({
            type: 'text',
            text: paramValues[`${param.type.toLowerCase()}_${i}`] ?? '',
          });
        }
        components.push({ type: param.type.toLowerCase(), parameters });
      }
    }

    return components;
  };

  const handleSubmit = async () => {
    if (!configurationId) return;

    if (channel === 'EMAIL') {
      if (!emailAddress.trim() || !emailBody.trim()) return;

      try {
        const conversation = await startConversation.mutateAsync({
          phoneNumber: emailAddress.trim(),
          configurationId,
          channel: 'EMAIL',
          message: emailBody.trim(),
          ...(emailSubject.trim() ? { templateName: emailSubject.trim() } : {}),
        });
        resetForm();
        onOpenChange(false);
        onCreated(conversation.id);
      } catch {
        // error handled by react-query
      }
      return;
    }

    if (!phoneNumber.trim() || !templateName) return;

    try {
      const components = templateParams.length > 0 ? buildComponents() : undefined;

      const conversation = await startConversation.mutateAsync({
        phoneNumber: phoneNumber.trim(),
        configurationId,
        channel: 'WHATSAPP',
        templateName,
        templateLanguage: selectedTemplate?.language ?? 'en_US',
        ...(components ? { components } : {}),
      });
      resetForm();
      onOpenChange(false);
      onCreated(conversation.id);
    } catch {
      // error handled by react-query
    }
  };

  const resetForm = () => {
    setChannel('WHATSAPP');
    setPhoneNumber('');
    setEmailAddress('');
    setEmailSubject('');
    setEmailBody('');
    setConfigurationId('');
    setTemplateName('');
    setParamValues({});
  };

  const handleTemplateChange = (name: string) => {
    setTemplateName(name);
    setParamValues({});
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetForm();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
          <DialogDescription>
            {channel === 'EMAIL'
              ? 'Start a new email conversation.'
              : 'Start a new WhatsApp conversation. A template message is required for first contact.'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto py-2">
          <div className="space-y-2">
            <p className="text-sm font-medium">Channel</p>
            <Select
              value={channel}
              onValueChange={(v: 'WHATSAPP' | 'EMAIL') => {
                setChannel(v);
                setTemplateName('');
                setParamValues({});
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                <SelectItem value="EMAIL">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {channel === 'EMAIL' ? (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Email Address</p>
                <Input
                  value={emailAddress}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmailAddress(e.target.value)
                  }
                  placeholder="recipient@example.com"
                  type="email"
                />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Configuration</p>
                <Select value={configurationId} onValueChange={setConfigurationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a configuration" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Subject</p>
                <Input
                  value={emailSubject}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmailSubject(e.target.value)
                  }
                  placeholder="Email subject (optional)"
                />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Message</p>
                <textarea
                  value={emailBody}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setEmailBody(e.target.value)
                  }
                  placeholder="Write your email message..."
                  rows={4}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Phone Number</p>
                <Input
                  value={phoneNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPhoneNumber(e.target.value)
                  }
                  placeholder="+441234567890"
                />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Configuration</p>
                <Select value={configurationId} onValueChange={setConfigurationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a configuration" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Template</p>
                <Select value={templateName} onValueChange={handleTemplateChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {approvedTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.name}>
                        {t.name} ({t.language})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {templateParams.length > 0 && (
                <div className="space-y-3 rounded-md border p-3">
                  <p className="text-muted-foreground text-xs font-medium uppercase">
                    Template Parameters
                  </p>
                  {templateParams.map((param) =>
                    param.format === 'IMAGE' ? (
                      <div key="header_image" className="space-y-1">
                        <p className="text-xs font-medium">Header Image URL</p>
                        <Input
                          value={paramValues['header_0'] ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setParamValues((prev) => ({ ...prev, header_0: e.target.value }))
                          }
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    ) : (
                      Array.from({ length: param.count }, (_, i) => (
                        <div key={`${param.type}_${i}`} className="space-y-1">
                          <p className="text-xs font-medium">
                            {param.type} Variable {`{{${i + 1}}}`}
                          </p>
                          <Input
                            value={paramValues[`${param.type.toLowerCase()}_${i}`] ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setParamValues((prev) => ({
                                ...prev,
                                [`${param.type.toLowerCase()}_${i}`]: e.target.value,
                              }))
                            }
                            placeholder={`Value for {{${i + 1}}}`}
                          />
                        </div>
                      ))
                    ),
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !configurationId ||
              startConversation.isPending ||
              (channel === 'EMAIL'
                ? !emailAddress.trim() || !emailBody.trim()
                : !phoneNumber.trim() || !templateName)
            }
          >
            {startConversation.isPending
              ? 'Sending...'
              : channel === 'EMAIL'
                ? 'Send Email'
                : 'Send Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
